import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  Input,
  ProgressBar,
  Text,
  Title1,
  Title3,
  Body1,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { Note } from '../models/note';
import { Module } from '../models/module';
import { getNotesByUserModules } from '../services/noteService';
import { useAppContext } from '../AppContext';
import TenderSettingsDrawer from './TenderSettings/TenderSettingsDrawer';

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

interface Filters {
  search: string;
  moduleIDs: number[];
  categories: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORIES: string[] = [
  'Uppföljning på plats',
  'Uppföljning digital',
  'Uppföljning administrativ',
  'Begärda uppgifter',
  'Inkomna handlingar',
  'Inkomna synpunkter',
  'Uppgifter från media',
  'Uppsägning av ramavtal',
  'Annans tillfälliga avstängning',
  'Anmodan om rättelse',
  'Synlighet',
  'Prio',
  'Övrigt',
];

const PAGE_SIZE = 50;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-CA');
}

function resolveParentModuleID(note: Note, tenders: Module[]): Note {
  if (note.parentModuleID) return note;
  const mod = tenders.find((m) => m.moduleID === note.noteModuleID);
  if (!mod) return note;
  const parentModuleID = Number(mod.modulePath.split('_')[2]);
  return { ...note, parentModuleID };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LogsTool: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAppContext();

  // Initialise filters from URL query params (mirrors Angular ngOnInit behaviour)
  const [filters, setFilters] = useState<Filters>(() => ({
    search: searchParams.get('search') ?? '',
    moduleIDs: searchParams.getAll('moduleIDs').map(Number),
    categories: searchParams.getAll('categories'),
  }));

  const [notes, setNotes] = useState<Note[]>([]);
  const [tenders, setTenders] = useState<Module[]>([]);
  const [tenderings, setTenderings] = useState<Module[]>([]);
  const [listIndex, setListIndex] = useState(0);
  const [notesLoading, setNotesLoading] = useState(false);
  const [moreNotesLoading, setMoreNotesLoading] = useState(false);
  const [noMoreNotes, setNoMoreNotes] = useState(false);
  const [selectedTender, setSelectedTender] = useState<Module | undefined>(undefined);
  const [isTenderDrawerOpen, setIsTenderDrawerOpen] = useState(false);

  // Store tenders in a ref so async callbacks always see the latest value
  const tendersRef = React.useRef<Module[]>([]);
  tendersRef.current = tenders;

  // ------------------------------------------------------------------
  // Data fetching
  // ------------------------------------------------------------------

  const applyParentModules = useCallback(
    (rawNotes: Note[], currentTenders: Module[]): Note[] =>
      rawNotes.map((n) => resolveParentModuleID(n, currentTenders)),
    []
  );

  const loadNotes = useCallback(
    async (pageIndex: number, currentFilters: Filters, currentTenders: Module[]) => {
      const res = await getNotesByUserModules(pageIndex, currentFilters);
      if (res.length < PAGE_SIZE) setNoMoreNotes(true);
      return applyParentModules(res, currentTenders);
    },
    [applyParentModules]
  );

  // Initial load
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setNotesLoading(true);
      try {
        const allModules: Module[] = user?.modules ?? [];
        const tenderModules = allModules.filter((m) => m.moduleType === 'Tender');
        const tenderingModules = allModules.filter((m) => m.moduleType === 'Tendering');
        setTenders(tenderModules);
        setTenderings(tenderingModules);

        const processed = await loadNotes(0, filters, tenderModules);
        if (cancelled) return;
        setNotes(processed);
      } finally {
        if (!cancelled) setNotesLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------------

  const search = async () => {
    setNotesLoading(true);
    setListIndex(0);
    setNoMoreNotes(false);
    try {
      const processed = await loadNotes(0, filters, tendersRef.current);
      setNotes(processed);
      setSearchParams(
        (prev) => { const n = new URLSearchParams(prev); n.set('search', filters.search); return n; },
        { replace: true }
      );
    } finally {
      setNotesLoading(false);
    }
  };

  const sortNotesBy = async (
    checked: boolean,
    value: string,
    property: 'categories' | 'moduleIDs'
  ) => {
    setNotesLoading(true);
    setListIndex(0);
    setNoMoreNotes(false);

    const newFilters: Filters = { ...filters };
    if (property === 'moduleIDs') {
      const numVal = Number(value);
      newFilters.moduleIDs = checked
        ? [...newFilters.moduleIDs, numVal]
        : newFilters.moduleIDs.filter((f) => f !== numVal);
    } else {
      newFilters.categories = checked
        ? [...newFilters.categories, value]
        : newFilters.categories.filter((f) => f !== value);
    }
    setFilters(newFilters);

    try {
      const processed = await loadNotes(0, newFilters, tendersRef.current);
      setNotes(processed);
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev);
          n.delete(property);
          const vals = property === 'moduleIDs'
            ? newFilters.moduleIDs.map(String)
            : newFilters.categories;
          vals.forEach((v) => n.append(property, v));
          return n;
        },
        { replace: true }
      );
    } finally {
      setNotesLoading(false);
    }
  };

  const loadNextNotes = async () => {
    setMoreNotesLoading(true);
    const nextIndex = listIndex + 1;
    setListIndex(nextIndex);
    try {
      const res = await getNotesByUserModules(nextIndex, filters);
      if (res.length < PAGE_SIZE) setNoMoreNotes(true);
      const processed = applyParentModules(res, tendersRef.current);
      setNotes((prev) => [...prev, ...processed]);
    } finally {
      setMoreNotesLoading(false);
    }
  };

  const removeFilters = async () => {
    const newFilters: Filters = { ...filters, categories: [], moduleIDs: [] };
    setFilters(newFilters);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('categories');
        n.delete('moduleIDs');
        return n;
      },
      { replace: true }
    );
    setNotesLoading(true);
    setListIndex(0);
    setNoMoreNotes(false);
    try {
      const processed = await loadNotes(0, newFilters, tendersRef.current);
      setNotes(processed);
    } finally {
      setNotesLoading(false);
    }
  };

  const openTenderDrawer = (moduleID: number) => {
    const module = tenders.find((m) => m.moduleID === moduleID);
    if (module) {
      setSelectedTender(module);
      setIsTenderDrawerOpen(true);
    }
  };

  // ------------------------------------------------------------------
  // Lookup helpers
  // ------------------------------------------------------------------

  const getTenderName = (id: number) =>
    tenders.find((m) => m.moduleID === id)?.moduleName ?? '';

  const getTenderingName = (id: number) =>
    tenderings.find((m) => m.moduleID === id)?.moduleName ?? '';

  const isFilterSelected = (
    property: 'categories' | 'moduleIDs',
    value: string | number
  ): boolean =>
    property === 'moduleIDs'
      ? filters.moduleIDs.includes(Number(value))
      : filters.categories.includes(value as string);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div className="bg-[#ebebeb] min-h-screen">
      <div className="flex flex-col w-full items-center h-36 bg-gradient-to-r from-orange-700 to-yellow-500 rounded-b mx-auto" />
      <div className="w-full max-w-[1480px] mx-auto px-4 -mt-10 flex flex-col gap-4 pb-8">
        {/* ── Search card ─────────────────────────────────────────────── */}
        <Card className="p-4">
          <Title1>Senaste loggböckerna</Title1>
          <div className="flex gap-2 mt-3 w-full max-w-md">
            <Input
              className="flex-1"
              placeholder="Sök loggbok"
              value={filters.search}
              disabled={notesLoading || moreNotesLoading}
              onChange={(_, d) =>
                setFilters((prev) => ({ ...prev, search: d.value }))
              }
              onKeyDown={(e) => e.key === 'Enter' && search()}
            />
            <Button
              appearance="primary"
              icon={<SearchRegular />}
              disabled={notesLoading || moreNotesLoading}
              onClick={search}
            />
          </div>
        </Card>

        {/* ── Content row ─────────────────────────────────────────────── */}
        <div className="flex gap-4 items-start">
        {/* Notes list */}
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          {notesLoading && (
            <ProgressBar />
          )}

          {!notesLoading && notes.length === 0 && (
            <Card className="p-8 flex flex-col items-center gap-2 text-center">
              <Body1 style={{ color: 'var(--colorNeutralForeground3)' }}>
                Inga loggböcker hittades.
              </Body1>
            </Card>
          )}

          {!notesLoading && notes.length > 0 && (
            <>
              {notes.map((note) => (
                <Card key={note.noteID} className="p-4">
                  <CardHeader
                    header={
                      <div className="flex flex-col gap-1">
                        <Body1>
                          {note.noteDate ? formatDate(note.noteDate) : ''} –{' '}
                          {getTenderName(note.noteModuleID)}
                        </Body1>
                        <Title3>{note.noteHeader ?? ''}</Title3>
                      </div>
                    }
                  />

                  <div className="flex gap-3 text-sm my-2">
                    <Text>{note.noteSubject}</Text>
                    {note.parentModuleID && (
                      <Text>{getTenderingName(note.parentModuleID)}</Text>
                    )}
                  </div>

                  {/*
                    Security: noteBody is rendered as raw HTML (mirrors original [innerHTML] binding).
                    Ensure the content is sanitised on the server before it reaches this component,
                    or pass it through DOMPurify.sanitize() before rendering.
                  */}
                  <div
                    className="my-2 prose max-w-none rich-text-content"
                    dangerouslySetInnerHTML={{ __html: note.noteBody }}
                  />

                  <div>
                    <span
                      className="text-blue-600 cursor-pointer hover:underline"
                      onClick={() => openTenderDrawer(note.noteModuleID)}
                    >
                      Läs hela loggboken...
                    </span>
                  </div>
                </Card>
              ))}

              <div className="flex flex-col gap-2">
                {!noMoreNotes ? (
                  <Button
                    appearance="primary"
                    className="w-full"
                    disabled={moreNotesLoading || notesLoading}
                    onClick={loadNextNotes}
                  >
                    Ladda fler loggböker
                  </Button>
                ) : (
                  <Body1 style={{ color: 'var(--colorNeutralForeground3)', textAlign: 'center' }}>
                    Inga fler loggböker att visa.
                  </Body1>
                )}
                {(moreNotesLoading || notesLoading) && <ProgressBar />}
              </div>
            </>
          )}
        </div>

        {/* ── Filters sidebar ───────────────────────────────────────── */}
        <Card className="w-64 shrink-0 p-4">
          <div className="flex flex-col gap-0">
            <Text weight="semibold">Kategori:</Text>

            {CATEGORIES.map((cat) => (
              <Checkbox
                key={cat}
                label={cat}
                checked={isFilterSelected('categories', cat)}
                disabled={notesLoading}
                onChange={(_, d) =>
                  sortNotesBy(!!d.checked, cat, 'categories')
                }
              />
            ))}

            <Text weight="semibold" className="mt-1">
              Ramavtal
            </Text>

            {tenderings
              .filter((tendering) =>
                tenders.some((tm) => +tm.modulePath.split('_')[2] === tendering.moduleID)
              )
              .map((tendering) => (
              <Checkbox
                key={tendering.moduleID}
                label={tendering.moduleName}
                checked={isFilterSelected('moduleIDs', tendering.moduleID)}
                disabled={notesLoading}
                onChange={(_, d) =>
                  sortNotesBy(
                    !!d.checked,
                    String(tendering.moduleID),
                    'moduleIDs'
                  )
                }
              />
            ))}

            <Button
              appearance="primary"
              className="mt-2"
              disabled={notesLoading}
              onClick={removeFilters}
            >
              Rensa filter
            </Button>
          </div>
        </Card>
      </div>      </div>
      {selectedTender && (
        <TenderSettingsDrawer
          open={isTenderDrawerOpen}
          onClose={() => { setIsTenderDrawerOpen(false); setSelectedTender(undefined); }}
          partialTender={selectedTender}
          tab="logs"
        />
      )}
    </div>
  );
};

export default LogsTool;
